// Operations Manager approval (0016_rs_replaces_aa_om_role.sql): pure helpers shared by the
// permit page, the board, the home list and the print/archive renderer. No React, no DOM,
// explicit .ts imports, so Deno (archive worker) and Node (tests, docs) load the same file.
//
// The server decides whether the approval is required and whether work may start; these
// helpers only read what the bundle already says.
import { ROLES } from './catalogue.ts'
import { authorisationChain } from './extension.ts'
import type { OmApprovalStatus, OmApprovalView, PermitBundle } from './types.ts'

/**
 * The declaration the OM signs (server-side text since 0023, mirrored here to show above the
 * checkbox). It states the 0020 rule that an RS-approved extension carries the approval over.
 * Approvals signed before 0023 keep the text they were signed with (OM_APPROVAL_DECLARATION_0016).
 */
export const OM_APPROVAL_DECLARATION =
  'As Operations Manager I have independently checked this authorised permit - its scope, location, hazards and controls, SIMOPS and the people named - and I approve it. No work may start under it until this approval is recorded. An RS-approved extension of this authorisation carries this approval over; a new authorisation needs a new approval.'

/** The declaration signed from 0016 until 0023 (kept on those signed records; never signed again). */
export const OM_APPROVAL_DECLARATION_0016 =
  'As Operations Manager I have independently checked this authorised permit - its scope, location, hazards and controls, SIMOPS and the people named - and I approve it. No work may start under it until this approval is recorded; a new authorisation needs a new approval.'

/** Blocker raised while the approval is missing (request_cycle, validate_cycle, accept_cycle, eligibility). */
export const OM_APPROVAL_REQUIRED_MESSAGE =
  'The Operations Manager has not approved this permit yet. No daily cycle can be requested and no work can start until the OM approves the authorised permit.'

type OmBundle = Pick<PermitBundle, 'om_approval_required' | 'om_approvals'> & {
  permit?: Pick<PermitBundle['permit'], 'current_authorisation_id'> | null
  authorisations?: PermitBundle['authorisations']
}

/**
 * The approval of the permit's current authorisation, or null. An RS-approved extension
 * carries over the approval of the authorisation it extends (0020), as the server does
 * (private.om_current_approval_id). Older bundles have none.
 */
export function currentOmApproval(b: OmBundle): OmApprovalView | null {
  const current = b.permit?.current_authorisation_id ?? null
  if (!current) return null
  const chain = b.authorisations ? authorisationChain({ authorisations: b.authorisations }, current).map((a) => a.id) : []
  const ids = chain.length > 0 ? chain : [current]
  for (const id of ids) {
    const found = (b.om_approvals ?? []).find((a) => a.authorisation_id === id)
    if (found) return found
  }
  return null
}

/** True when the current authorisation is an extension whose OM approval was given to the authorisation it extends. */
export function omApprovalCarriedOver(b: OmBundle): boolean {
  const a = currentOmApproval(b)
  return a !== null && a.authorisation_id !== (b.permit?.current_authorisation_id ?? null)
}

/**
 * 'not_required' when the permit's policy has no OM step (missing = not required, as on
 * bundles from before 0016), 'approved' when the current authorisation has an approval,
 * else 'pending' (also before the permit is authorised).
 */
export function omApprovalStatus(b: OmBundle): OmApprovalStatus {
  if (b.om_approval_required !== true) return 'not_required'
  return currentOmApproval(b) ? 'approved' : 'pending'
}

export const OM_STATUS_TEXT: Record<OmApprovalStatus, { label: string; tone: 'ok' | 'warn' | 'neutral' }> = {
  approved: { label: 'OM approved', tone: 'ok' },
  pending: { label: 'OM approval pending', tone: 'warn' },
  not_required: { label: 'No OM approval needed', tone: 'neutral' },
}

/** One plain sentence for the permit page. `when` formats the server time. */
export function omApprovalSentence(b: OmBundle, when: (iso: string) => string): string {
  const status = omApprovalStatus(b)
  if (status === 'not_required') return "Operations Manager approval: not required for this permit's policy."
  if (status === 'pending') {
    return b.permit?.current_authorisation_id
      ? 'Operations Manager approval: pending - no work can start until the OM approves.'
      : 'Operations Manager approval: needed once the RS authorises the permit - no work can start until the OM approves.'
  }
  const a = currentOmApproval(b) as OmApprovalView
  const carried = omApprovalCarriedOver(b) ? ' It carries over to the RS-approved extension.' : ''
  return `Operations Manager approval: approved by ${a.attestation.signer.display_name} at ${when(a.attestation.server_signed_at)}.${carried}`
}

/**
 * Display name of a role code. 'AA' reads "Area Authority (historic)": BadrEOR has no Area
 * Authority from 0016, so an AA signature, confirmation or capacity is always an older record.
 */
export function roleDisplayName(code: string | null | undefined): string {
  if (!code) return '-'
  return (ROLES as Record<string, { label: string } | undefined>)[code]?.label ?? code
}
